/* global globalThis */
import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Popover,
  TextField,
  Alert,
  Tooltip,
} from "@mui/material";
import NoteIcon from "../../assets/notes.svg";
import CommonSelect from "../common/CommonSelect";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import MinisizeIcon from "../../assets/minus-circle.svg";
import ConfirmationDialog from "../ConfirmationDialog";
import SaveIcon from "../../assets/save.svg";
import { completeManualTask } from "../../api/manualTask";

const statusOptions = ["Eligible", "Enrolled", "Declined"];
const outreachMethods = ["Phone Call", "Email", "SMS"];
const outreachOutcomes = [
  "Reached",
  "Not Reached - Left Message",
  "Not Reached - No Message",
  "Scheduled Callback",
  "Do Not Call",
];

const providerOutreachOptions = [
  "Provider consultation",
  "Provider Education",
  "Provider Follow-up",
  "Dosing Optimization",
];

const pharmacyOutreachOutcomes = [
  "Reached",
  "Not Reached - Left Message",
  "Not Reached - No Message",
];
const pharmacyOutreachMethods = ["Phone Call", "Email", "SMS"];
const pharmacyOutreachOptions = [
  "Request Prescription",
  "Confirm RX Received",
  "Pharmacy Consultation",
];
const pharmacyStatusOptions = ["Resolved", "Not Resolved", "Declined"];
const pharmacyMemberOptions = [];

function mapStatus(status) {
  if (status === "ENROLLED") return "Enrolled";
  if (status === "ELIGIBLE") return "Eligible";
  if (status === "DECLINED") return "Declined";
  return status || "";
}

const getOutreachOptions = () => {
  const path = globalThis.location.pathname.toLowerCase();
  if (path.includes("pharmacyprofile") || path.includes("pharmacy-outreach")) {
    return pharmacyOutreachOptions;
  }
  return providerOutreachOptions;
};

const getOutreachStatusOptions = () => {
  const path = globalThis.location.pathname.toLowerCase();
  if (
    path.includes("pharmacyprofile") ||
    path.includes("providerprofile") ||
    path.includes("pharmacy-outreach")
  ) {
    return pharmacyStatusOptions;
  }
  return statusOptions;
};
const getOutreachMethods = () => {
  const path = globalThis.location.pathname.toLowerCase();
  if (
    path.includes("pharmacyprofile") ||
    path.includes("providerprofile") ||
    path.includes("pharmacy-outreach")
  ) {
    return pharmacyOutreachMethods;
  }
  return outreachMethods;
};
const getOutreachOutcomes = (status) => {
  const path = globalThis.location.pathname.toLowerCase();
  if (
    path.includes("pharmacyprofile") ||
    path.includes("providerprofile") ||
    path.includes("pharmacy-outreach")
  ) {
    return pharmacyOutreachOutcomes;
  }

  if (status && status !== "Declined") {
    return outreachOutcomes.filter((option) => option !== "Do Not Call");
  }

  return outreachOutcomes;
};

const emptyRow = {
  program: "",
  medication: "",
  reasonForOutreach: "",
  status: "",
  method: "",
  outcome: "",
  note: "",
  error: false,
};



const getInitialManualTaskId = (row, idx, dispositionOpportunity) => {
  if (Array.isArray(dispositionOpportunity) && dispositionOpportunity[idx]) {
    return (
      dispositionOpportunity[idx].manualTaskId || dispositionOpportunity[idx].id
    );
  }
  if (
    dispositionOpportunity &&
    Array.isArray(dispositionOpportunity.selected) &&
    dispositionOpportunity.selected[idx]
  ) {
    return (
      dispositionOpportunity.selected[idx].manualTaskId ||
      dispositionOpportunity.selected[idx].id
    );
  }
  if (
    dispositionOpportunity?.data &&
    Array.isArray(dispositionOpportunity.data) &&
    dispositionOpportunity.data[idx]
  ) {
    return (
      dispositionOpportunity.data[idx].manualTaskId ||
      dispositionOpportunity.data[idx].id
    );
  }
  if (row.manualTaskId) {
    return row.manualTaskId;
  }
  if (row.id) {
    return row.id;
  }
  return null;
};

const refineManualTaskIdWithMember = (
  currentManualTaskId,
  row,
  memberDrugMap,
) => {
  if (
    !row.program ||
    !memberDrugMap ||
    !(row.program in memberDrugMap) ||
    !Array.isArray(memberDrugMap[row.program].members)
  ) {
    return currentManualTaskId;
  }

  let selectedMemberName = row.program;
  if (row.member) {
    selectedMemberName = row.member;
  } else if (
    row.program &&
    memberDrugMap[row.program].members.length === 1
  ) {
    selectedMemberName = memberDrugMap[row.program].members[0].name;
  }

  const foundMember = memberDrugMap[row.program].members.find(
    (m) => m.name === selectedMemberName,
  );

  if (foundMember?.manualTaskId) {
    return foundMember.manualTaskId;
  }

  return currentManualTaskId;
};

const createDispositionPayload = (
  row,
  idx,
  dispositionOpportunity,
  memberDrugMap,
  globalNote,
  type,
) => {
  const initialManualTaskId = getInitialManualTaskId(
    row,
    idx,
    dispositionOpportunity,
  );
  const manualTaskId = refineManualTaskIdWithMember(
    initialManualTaskId,
    row,
    memberDrugMap,
  );

  return {
    manualTaskId: manualTaskId,
    outReachMethod: row.method,

    outreachReason: row.reasonForOutreach
      ? row.reasonForOutreach.replaceAll(/[\s-]+/g, "_").toUpperCase()
      : "",
    dispositionOutcome: row.outcome ? row.outcome : "",
    memberNote: row.note,
    outreachNote: globalNote || "",

    dispositionStatus: (row.status || "")
      .toUpperCase()
      .replaceAll(/[\s-]+/g, "_"),
    recipientType: type === "provider" ? "PROVIDER" : "PHARMACY",
  };
};

const ProgramCell = ({
  row,
  idx,
  title,
  isOutreachContext,
  originalRowCount,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  memberDrugMap,
  pharmacyMemberOptions,
  getAvailableProgramsForRow,
  handleSelectValue,
}) => (
  <TableCell
    sx={{
      ...((title === "Manage Disposition" && idx < originalRowCount) ||
      title === "Add Disposition"
        ? { backgroundColor: "#E8E8E9" }
        : undefined),
      cursor:
        (title === "Manage Disposition" && idx < originalRowCount) ||
        title === "Add Disposition"
          ? "not-allowed"
          : "pointer",
    }}
  >
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: (() => {
          if (title === "Add Disposition" && row.program) {
            return "#9F9DB6";
          } else if (row.program) {
            return "#403C6F";
          } else {
            return "#9F9DB6";
          }
        })(),
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
        pointerEvents:
          (title === "Manage Disposition" && idx < originalRowCount) ||
          title === "Add Disposition"
            ? "none"
            : "auto",
        cursor:
          (title === "Manage Disposition" && idx < originalRowCount) ||
          title === "Add Disposition"
            ? "not-allowed"
            : "pointer",
        backgroundColor: "transparent",
      }}
      onClick={(e) =>
        title !== "Add Disposition" && handleSelectOpen(e, idx, "program")
      }
      endIcon={
        title !== "Add Disposition" && (
          <ExpandMoreIcon sx={{ color: "#9F9DB6" }} />
        )
      }
      disabled={
        (title === "Manage Disposition" && idx < originalRowCount) ||
        title === "Add Disposition"
      }
    >
      <span>
        {row.program || (
          <span style={{ color: "#9F9DB6" }}>
            {isOutreachContext() ? "Select member" : "Select program"}
          </span>
        )}
      </span>
    </Button>
    {selectPopover.idx === idx && selectPopover.field === "program" && (
      <CommonSelect
        id={`program-popover-${idx}`}
        open={Boolean(selectPopover.anchorEl)}
        anchorEl={selectPopover.anchorEl}
        onClose={handleSelectClose}
        inQuickview={false}
        filterButtonRef={{
          current: selectPopover.anchorEl,
        }}
        noteTypes={(() => {
          if (
            isOutreachContext() &&
            memberDrugMap &&
            Object.keys(memberDrugMap).length > 0
          ) {
            return (
              memberDrugMap[
                row.program || Object.keys(memberDrugMap)[0]
              ]?.members?.map((m) => m.name) || []
            );
          } else if (
            globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacyprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("providerprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacy-outreach")
          ) {
            return pharmacyMemberOptions;
          } else {
            return getAvailableProgramsForRow(idx);
          }
        })()}
        selectedTypes={row.program ? [row.program] : []}
        handleSelectAll={() => {
          let options;
          if (
            isOutreachContext() &&
            memberDrugMap &&
            Object.keys(memberDrugMap).length > 0
          ) {
            options =
              memberDrugMap[
                row.program || Object.keys(memberDrugMap)[0]
              ]?.members?.map((m) => m.name) || [];
          } else if (
            globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacyprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("providerprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacy-outreach")
          ) {
            options = pharmacyMemberOptions;
          } else {
            options = getAvailableProgramsForRow(idx);
          }
          handleSelectValue(options[0]);
        }}
        handleTypeToggle={(val) => handleSelectValue(val)}
      />
    )}
  </TableCell>
);

ProgramCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  title: PropTypes.string,
  isOutreachContext: PropTypes.func.isRequired,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  memberDrugMap: PropTypes.object,
  pharmacyMemberOptions: PropTypes.array,
  getAvailableProgramsForRow: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
};

const ReasonCell = ({
  row,
  idx,
  title,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  getOutreachOptions,
  handleSelectValue,
}) => (
  <TableCell
    sx={{
      ...(title === "Add Disposition"
        ? { backgroundColor: "#E8E8E9" }
        : undefined),
      cursor: title === "Add Disposition" ? "not-allowed" : "pointer",
    }}
  >
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: (() => {
          if (title === "Add Disposition" && row.reasonForOutreach) {
            return "#9F9DB6";
          } else if (row.reasonForOutreach) {
            return "#403C6F";
          } else {
            return "#9F9DB6";
          }
        })(),
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
        pointerEvents: title === "Add Disposition" ? "none" : "auto",
      }}
      onClick={(e) =>
        title !== "Add Disposition" &&
        handleSelectOpen(e, idx, "reasonForOutreach")
      }
      endIcon={
        title !== "Add Disposition" && (
          <ExpandMoreIcon sx={{ color: "#9F9DB6" }} />
        )
      }
      disabled={title === "Add Disposition"}
    >
      <span>
        {row.reasonForOutreach || (
          <span style={{ color: "#9F9DB6" }}>Select reason</span>
        )}
      </span>
    </Button>
    {selectPopover.idx === idx &&
      selectPopover.field === "reasonForOutreach" && (
        <CommonSelect
          id={`reason-popover-${idx}`}
          open={Boolean(selectPopover.anchorEl)}
          anchorEl={selectPopover.anchorEl}
          onClose={handleSelectClose}
          inQuickview={false}
          filterButtonRef={{
            current: selectPopover.anchorEl,
          }}
          noteTypes={getOutreachOptions()}
          selectedTypes={row.reasonForOutreach ? [row.reasonForOutreach] : []}
          handleSelectAll={() => handleSelectValue(getOutreachOptions()[0])}
          handleTypeToggle={(val) => handleSelectValue(val)}
        />
      )}
  </TableCell>
);

ReasonCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  title: PropTypes.string,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  getOutreachOptions: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
};

const MedicationCell = ({
  row,
  idx,
  title,
  originalRowCount,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  getMedicationOptionsForRow,
  handleSelectValue,
  getMonitoringReasonForDrug,
  rows,
}) => (
  <TableCell
    sx={{
      ...(title === "Manage Disposition" && idx < originalRowCount
        ? { backgroundColor: "#E8E8E9" }
        : undefined),
      cursor:
        title === "Manage Disposition" && idx < originalRowCount
          ? "not-allowed"
          : "pointer",
    }}
  >
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: row.medication ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
        pointerEvents:
          (title === "Manage Disposition" && idx < originalRowCount) ||
          !row.program
            ? "none"
            : "auto",
        cursor:
          (title === "Manage Disposition" && idx < originalRowCount) ||
          !row.program
            ? "not-allowed"
            : "pointer",
        backgroundColor: "transparent",
        opacity: row.program ? 1 : 0.5,
      }}
      onClick={(e) => {
        if (row.program) {
          handleSelectOpen(e, idx, "medication");
        }
      }}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
      disabled={
        (title === "Manage Disposition" && idx < originalRowCount) ||
        !row.program
      }
    >
      <span>
        {(() => {
          if (typeof row.medication === "object") {
            return row.medication.drug;
          } else if (
            typeof row.medication === "string" &&
            row.medication.includes("-")
          ) {
            return row.medication.split("-")[0];
          } else if (row.medication) {
            return row.medication;
          } else {
            return <span style={{ color: "#9F9DB6" }}>Select medication</span>;
          }
        })()}
      </span>
    </Button>
    {selectPopover.idx === idx &&
      selectPopover.field === "medication" &&
      row.program && (
        <CommonSelect
          id={`medication-popover-${idx}`}
          open={Boolean(selectPopover.anchorEl)}
          anchorEl={selectPopover.anchorEl}
          onClose={handleSelectClose}
          inQuickview={false}
          filterButtonRef={{
            current: selectPopover.anchorEl,
          }}
          noteTypes={getMedicationOptionsForRow(
            rows[idx].program,
            idx
          ).map((drugObj) => drugObj.drug)}
          selectedTypes={
            row.medication
              ? (() => {
                  let medicationValue;
                  if (typeof row.medication === "object") {
                    medicationValue = row.medication.drug;
                  } else if (
                    typeof row.medication === "string" &&
                    row.medication.includes("-")
                  ) {
                    medicationValue = row.medication.split("-")[0];
                  } else {
                    medicationValue = row.medication;
                  }
                  return [medicationValue].filter(Boolean);
                })()
              : []
          }
          handleSelectAll={() =>
            handleSelectValue(
              getMedicationOptionsForRow(rows[idx].program, idx)[0]?.drug
            )
          }
          handleTypeToggle={(val) => {
            const drugObj = getMedicationOptionsForRow(
              rows[idx].program,
              idx
            ).find((d) => d.drug === val);

            const monitoringReason = getMonitoringReasonForDrug(
              rows[idx].program,
              val
            );

            const valueWithMonitoring = drugObj
              ? {
                  ...drugObj,
                  monitoringReason: monitoringReason,
                }
              : val;

            handleSelectValue(valueWithMonitoring);
          }}
        />
      )}
  </TableCell>
);

MedicationCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  getMedicationOptionsForRow: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
  getMonitoringReasonForDrug: PropTypes.func.isRequired,
  rows: PropTypes.array.isRequired,
};

const MonitoringReasonCell = ({ row, idx, handleChange }) => (
  <TableCell>
    <Tooltip
      title={row.monitoringReason || ""}
      arrow
      placement="bottom-end"
      slotProps={{
        popper: {
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#343435",
              color: "#FFFFFF",
            },
            "& .MuiTooltip-arrow": {
              color: "#343435",
              left: "50% !important",
              transform: "translateX(-50%) !important",
            },
          },
        },
      }}
    >
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        value={row.monitoringReason || ""}
        onChange={(e) => handleChange(idx, "monitoringReason", e.target.value)}
        slotProps={{
          input: {
            sx: {
              fontSize: "0.8rem",
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              width: "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #403C6F",
              },
              "& .MuiInputBase-input": {
                padding: "0px",
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            },
          },
        }}
        disabled={true}
        placeholder="Monitoring reason will auto-fill"
      />
    </Tooltip>
  </TableCell>
);

MonitoringReasonCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  handleChange: PropTypes.func.isRequired,
};

const StatusCell = ({
  row,
  idx,
  title,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  showMonitoringReason,
  getStatusOptions,
  initialRows,
  getOutreachStatusOptions,
  handleSelectValue,
}) => (
  <TableCell>
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: row.status ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
      }}
      onClick={(e) => handleSelectOpen(e, idx, "status")}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
    >
      <span>
        {row.status || <span style={{ color: "#9F9DB6" }}>Select status</span>}
      </span>
    </Button>
    {selectPopover.idx === idx && selectPopover.field === "status" && (
      <CommonSelect
        id={`status-popover-${idx}`}
        open={Boolean(selectPopover.anchorEl)}
        anchorEl={selectPopover.anchorEl}
        onClose={handleSelectClose}
        inQuickview={false}
        filterButtonRef={{
          current: selectPopover.anchorEl,
        }}
        noteTypes={(() => {
          let statusOptions;
          if (showMonitoringReason) {
            statusOptions = getStatusOptions(idx);
          } else if (
            ["Declined", "Eligible", "Resolved", "Not Resolved"].includes(
              initialRows[idx]?.status || ""
            )
          ) {
            if (
              initialRows[idx]?.status === "Enrolled" ||
              initialRows[idx]?.status === "Declined" ||
              initialRows[idx]?.status === "Resolved" ||
              initialRows[idx]?.status === "Not Resolved"
            ) {
              statusOptions = ["Declined", "Resolved", "Not Resolved"];
            } else {
              statusOptions = ["Eligible", "Enrolled", "Declined"];
            }
          } else {
            statusOptions = getOutreachStatusOptions().filter((option) =>
              initialRows[idx]?.status === "Enrolled"
                ? option !== "Eligible"
                : true
            );
          }
          return statusOptions;
        })()}
        selectedTypes={row.status ? [row.status] : []}
        handleSelectAll={() => {
          let selectValue;
          if (showMonitoringReason) {
            selectValue = getStatusOptions(idx)[0];
          } else if (
            title === "Manage Disposition" &&
            [
              "Declined",
              "Enrolled",
              "Eligible",
              "Resolved",
              "Not Resolved",
            ].includes(initialRows[idx]?.status || "")
          ) {
            selectValue = "Declined";
          } else {
            selectValue = getOutreachStatusOptions()[0];
          }
          handleSelectValue(selectValue);
        }}
        handleTypeToggle={(val) => handleSelectValue(val)}
      />
    )}
  </TableCell>
);

StatusCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  title: PropTypes.string,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  showMonitoringReason: PropTypes.bool,
  getStatusOptions: PropTypes.func.isRequired,
  initialRows: PropTypes.array,
  getOutreachStatusOptions: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
};

const MethodCell = ({
  row,
  idx,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  getOutreachMethods,
  handleSelectValue,
}) => (
  <TableCell>
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: row.method ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
      }}
      onClick={(e) => handleSelectOpen(e, idx, "method")}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
    >
      <span>
        {row.method || <span style={{ color: "#9F9DB6" }}>Select method</span>}
      </span>
    </Button>
    {selectPopover.idx === idx && selectPopover.field === "method" && (
      <CommonSelect
        id={`method-popover-${idx}`}
        open={Boolean(selectPopover.anchorEl)}
        anchorEl={selectPopover.anchorEl}
        onClose={handleSelectClose}
        inQuickview={false}
        filterButtonRef={{
          current: selectPopover.anchorEl,
        }}
        noteTypes={getOutreachMethods()}
        selectedTypes={row.method ? [row.method] : []}
        handleSelectAll={() => handleSelectValue(getOutreachMethods()[0])}
        handleTypeToggle={(val) => handleSelectValue(val)}
      />
    )}
  </TableCell>
);

MethodCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  getOutreachMethods: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
};

const OutcomeCell = ({
  row,
  idx,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  handleSelectValue,
}) => (
  <TableCell>
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: row.outcome ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
      }}
      onClick={(e) => handleSelectOpen(e, idx, "outcome")}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
    >
      <span>
        {row.outcome || <span style={{ color: "#9F9DB6" }}>Select outcome</span>}
      </span>
    </Button>
    {selectPopover.idx === idx && selectPopover.field === "outcome" && (
      <CommonSelect
        id={`outcome-popover-${idx}`}
        open={Boolean(selectPopover.anchorEl)}
        anchorEl={selectPopover.anchorEl}
        onClose={handleSelectClose}
        inQuickview={false}
        filterButtonRef={{
          current: selectPopover.anchorEl,
        }}
        noteTypes={getOutreachOutcomes(row.status)}
        selectedTypes={row.outcome ? [row.outcome] : []}
        handleSelectAll={() => {
          const outcomes = getOutreachOutcomes(row.status);
          handleSelectValue(outcomes.length > 0 ? outcomes[0] : "");
        }}
        handleTypeToggle={(val) => handleSelectValue(val)}
      />
    )}
  </TableCell>
);

OutcomeCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
};

const NoteCell = ({
  row,
  idx,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  handleNoteSave,
}) => (
  <TableCell
    sx={{
      textAlign: "center",
      position: "relative",
      zIndex: 2201,
      p: "0 !important",
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          noteAnchor && noteRowIdx === idx ? "#fff" : "transparent",
        transition: "background 0.2s, box-shadow 0.2s",
        position: "relative",
        zIndex: 2001,
        m: 0,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:
            noteAnchor && noteRowIdx === idx
              ? "0 0 10px rgba(64,60,111,0.6)"
              : "none",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleNoteClick(e, idx);
          }}
          size="small"
          sx={{ color: row.note ? "#403C6F" : "#9F9DB6" }}
        >
          <img src={NoteIcon} alt="notes" />
        </IconButton>
      </Box>
    </Box>
    <Popover
      open={Boolean(noteAnchor) && noteRowIdx === idx}
      anchorEl={noteAnchor}
      onClose={handleNoteClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            mt: 0.4,
            width: 350,
            backgroundColor: "#F7F8FB",
            height: "290px",
            zIndex: 1000,
            borderRadius: "12px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
          },
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <TextField
          multiline
          minRows={2}
          value={noteValue}
          onChange={(e) => setNoteValue(e.target.value.slice(0, 512))}
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            backgroundColor: "#FFFFFF",
            width: "100%",
            minHeight: "200px",
            borderRadius: "8px",
            boxShadow: "0px 0px 2px 0px #00000040",
            overflow: "auto",
            "& .MuiOutlinedInput-root": {
              height: "100%",
              alignItems: "flex-start",
              padding: 0,
              "& textarea": {
                padding: "16px",
                boxSizing: "border-box",
                overflow: "auto !important",
                maxHeight: "calc(200px - 32px)",
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          placeholder="Enter note details"
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            fontSize: "12px",
            color: noteValue.length > 512 ? "#d32f2f" : "#666666",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "2px 6px",
            borderRadius: "4px",
            pointerEvents: "none",
          }}
        >
          {noteValue.length}/512
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 1,
          gap: 1,
        }}
      >
        <Button
          onClick={handleNoteClose}
          variant="outlined"
          sx={{
            height: 38,
            borderRadius: "52px",
            border: "1px solid #9F9DB6",
            color: "#403C6F",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleNoteSave}
          variant="contained"
          sx={{
            height: 38,
            borderRadius: "52px",
            backgroundColor: "#3F3D56",
            "&:hover": { backgroundColor: "#2F2C46" },
          }}
        >
          Save
        </Button>
      </Box>
    </Popover>
  </TableCell>
);

NoteCell.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func.isRequired,
  handleNoteClose: PropTypes.func.isRequired,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func.isRequired,
  handleNoteSave: PropTypes.func.isRequired,
};

const DeleteCell = ({ idx, rows, handleDeleteRow }) => (
  <TableCell sx={{ textAlign: "center" }}>
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        handleDeleteRow(idx);
      }}
      size="small"
      sx={{
        color: rows.length <= 1 ? "#9F9DB6" : "#F44336",
        "&:disabled": {
          color: "#9F9DB6",
          cursor: "not-allowed",
          pointerEvents: "auto",
        },
      }}
      disabled={rows.length <= 1}
      aria-label="delete row"
    >
      <img
        src={MinisizeIcon}
        alt="delete"
        style={{
          opacity: rows.length <= 1 ? 0.5 : 1,
          cursor: rows.length <= 1 ? "not-allowed" : "pointer",
        }}
      />
    </IconButton>
  </TableCell>
);

DeleteCell.propTypes = {
  idx: PropTypes.number.isRequired,
  rows: PropTypes.array.isRequired,
  handleDeleteRow: PropTypes.func.isRequired,
};

const DispositionRow = ({
  row,
  idx,
  title,
  isOutreachContext,
  handleRowClick,
  selectedRow,
  originalRowCount,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  memberDrugMap,
  pharmacyMemberOptions,
  getAvailableProgramsForRow,
  handleSelectValue,
  getMedicationOptionsForRow,
  getMonitoringReasonForDrug,
  memberBadgeValue,
  showMonitoringReason,
  handleChange,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  handleNoteSave,
  handleDeleteRow,
  rows,
  getOutreachOptions,
  getOutreachMethods,
  initialRows,
  getOutreachStatusOptions,
  getStatusOptions,
}) => {
  return (
    <TableRow
      hover
      onClick={() => handleRowClick(idx)}
      sx={{
        background: (() => {
          if (row.error) {
            return "#FFF0F0";
          } else if (selectedRow === idx) {
            return "#F5F5FF";
          } else {
            return "inherit";
          }
        })(),
        cursor: "pointer",
      }}
    >
      <ProgramCell
        row={row}
        idx={idx}
        title={title}
        isOutreachContext={isOutreachContext}
        originalRowCount={originalRowCount}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        memberDrugMap={memberDrugMap}
        pharmacyMemberOptions={pharmacyMemberOptions}
        getAvailableProgramsForRow={getAvailableProgramsForRow}
        handleSelectValue={handleSelectValue}
      />

      {isOutreachContext() ? (
        <ReasonCell
          row={row}
          idx={idx}
          title={title}
          handleSelectOpen={handleSelectOpen}
          selectPopover={selectPopover}
          handleSelectClose={handleSelectClose}
          getOutreachOptions={getOutreachOptions}
          handleSelectValue={handleSelectValue}
        />
      ) : (
        <MedicationCell
          row={row}
          idx={idx}
          title={title}
          originalRowCount={originalRowCount}
          handleSelectOpen={handleSelectOpen}
          selectPopover={selectPopover}
          handleSelectClose={handleSelectClose}
          getMedicationOptionsForRow={getMedicationOptionsForRow}
          handleSelectValue={handleSelectValue}
          getMonitoringReasonForDrug={getMonitoringReasonForDrug}
          rows={rows}
        />
      )}
      {(memberBadgeValue === "Member Monitoring" || showMonitoringReason) && (
        <MonitoringReasonCell
          row={row}
          idx={idx}
          handleChange={handleChange}
        />
      )}

      <StatusCell
        row={row}
        idx={idx}
        title={title}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        showMonitoringReason={showMonitoringReason}
        getStatusOptions={getStatusOptions}
        initialRows={initialRows}
        getOutreachStatusOptions={getOutreachStatusOptions}
        handleSelectValue={handleSelectValue}
      />

      <MethodCell
        row={row}
        idx={idx}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        getOutreachMethods={getOutreachMethods}
        handleSelectValue={handleSelectValue}
      />

      <OutcomeCell
        row={row}
        idx={idx}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        handleSelectValue={handleSelectValue}
      />

      <NoteCell
        row={row}
        idx={idx}
        noteAnchor={noteAnchor}
        noteRowIdx={noteRowIdx}
        handleNoteClick={handleNoteClick}
        handleNoteClose={handleNoteClose}
        noteValue={noteValue}
        setNoteValue={setNoteValue}
        handleNoteSave={handleNoteSave}
      />

      <DeleteCell idx={idx} rows={rows} handleDeleteRow={handleDeleteRow} />
    </TableRow>
  );
};

DispositionRow.propTypes = {
  row: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  title: PropTypes.string,
  isOutreachContext: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  selectedRow: PropTypes.number,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func.isRequired,
  selectPopover: PropTypes.object.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  memberDrugMap: PropTypes.object,
  pharmacyMemberOptions: PropTypes.array,
  getAvailableProgramsForRow: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
  getMedicationOptionsForRow: PropTypes.func.isRequired,
  getMonitoringReasonForDrug: PropTypes.func.isRequired,
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func.isRequired,
  handleNoteClose: PropTypes.func.isRequired,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func.isRequired,
  handleNoteSave: PropTypes.func.isRequired,
  handleDeleteRow: PropTypes.func.isRequired,
  rows: PropTypes.array.isRequired,
  getOutreachOptions: PropTypes.func.isRequired,
  getOutreachMethods: PropTypes.func.isRequired,
  initialRows: PropTypes.array,
  getOutreachStatusOptions: PropTypes.func,
  getStatusOptions: PropTypes.func,
};

const OutreachDispositionModal = ({
  open,
  onClose,
  onSave,
  title,
  dispositionOpportunity,
  programOptions,
  programDrugMap,
  showMonitoringReason,
  memberBadgeValue,
  memberDrugMap,
  type,
}) => {
  const isOutreachContext = useCallback(() => {
    const path = globalThis.location.pathname.toLowerCase();
    return (
      path.includes("providerprofile") ||
      path.includes("pharmacyprofile") ||
      title === "Provider Outreach" ||
      title === "Pharmacy Outreach"
    );
  }, [title]);
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [noteAnchor, setNoteAnchor] = useState(null);
  const [noteRowIdx, setNoteRowIdx] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [globalNote, setGlobalNote] = useState("");
  const GLOBAL_NOTE_CHAR_LIMIT = 512;

  const canSubmit = useMemo(() => {
    if (isSubmitting) return false;
    if (!rows || rows.length === 0) return false;

    const isCtx =
      isOutreachContext() &&
      memberDrugMap &&
      Object.keys(memberDrugMap).length > 0;
    const requiredOutreach = [
      "program",
      "reasonForOutreach",
      "status",
      "method",
      "outcome",
    ];
    const requiredDefault = [
      "program",
      "medication",
      "status",
      "method",
      "outcome",
    ];
    return rows.every((row) => {
      const keys = isCtx ? requiredOutreach : requiredDefault;
      return (
        !row.error &&
        keys.every((k) => {
          const val = row[k];
          if (val === null || val === undefined) return false;
          if (typeof val === "object") return true;
          return String(val).trim() !== "";
        })
      );
    });
  }, [rows, isSubmitting, memberDrugMap, isOutreachContext]);

  const [selectPopover, setSelectPopover] = useState({
    idx: null,
    field: null,
    anchorEl: null,
  });

  const [initialRows, setInitialRows] = useState([]);
  const [originalRowCount, setOriginalRowCount] = useState(0);
  const prevOpenRef = React.useRef(false);

  const buildFilledRow = React.useCallback((opp, title) => {
    const baseRow = {
      ...emptyRow,
      program: opp.category || "",
      medication: opp.drug || "",
      reasonForOutreach: opp.outreachReason || "",
      status: mapStatus(opp.status),
      monitoringReason: opp.monitoringReason || "",
    };
    if (title === "Add Disposition") {
      return baseRow;
    }
    return {
      ...baseRow,
      method: opp.method || "",
      outcome: opp.recentActivity?.status || "",
    };
  }, []);

  const buildFilledRows = React.useCallback(
    (oppArray, title) => {
      return oppArray.map((opp) => buildFilledRow(opp, title));
    },
    [buildFilledRow],
  );

  const getOpportunityArray = React.useCallback((dispositionOpportunity) => {
    if (Array.isArray(dispositionOpportunity)) {
      return dispositionOpportunity;
    }
    if (
      dispositionOpportunity?.selected &&
      Array.isArray(dispositionOpportunity?.selected) &&
      dispositionOpportunity?.selected.length > 0
    ) {
      return dispositionOpportunity.selected;
    }
    if (
      dispositionOpportunity?.data &&
      Array.isArray(dispositionOpportunity?.data) &&
      dispositionOpportunity?.data.length > 0
    ) {
      return dispositionOpportunity.data;
    }
    return [];
  }, []);

  const setRowsFromOpportunities = React.useCallback(
    (opps, title) => {
      if (opps.length > 0) {
        const filledRows = buildFilledRows(opps, title);
        setRows(filledRows);
        setInitialRows(filledRows);
        setOriginalRowCount(filledRows.length);
      }
    },
    [buildFilledRows],
  );

  React.useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (justOpened) {
      if (dispositionOpportunity) {
        const oppArray = getOpportunityArray(dispositionOpportunity);
        if (oppArray.length > 0) {
          setRowsFromOpportunities(oppArray, title);
        } else if (
          typeof dispositionOpportunity === "object" &&
          !Array.isArray(dispositionOpportunity)
        ) {
          const opp = dispositionOpportunity;
          const filledRow = buildFilledRow(opp, title);
          setRows([filledRow]);
          setInitialRows([filledRow]);
          setOriginalRowCount(1);
        }
      } else {
        setRows([{ ...emptyRow }]);
        setInitialRows([{ ...emptyRow }]);
        setOriginalRowCount(0);
      }
    }
  }, [
    open,
    dispositionOpportunity,
    title,
    buildFilledRow,
    buildFilledRows,
    getOpportunityArray,
    setRowsFromOpportunities,
  ]);

  React.useEffect(() => {
    if (open) {
      const newRows =
        initialRows.length > 0
          ? initialRows.map((r) => ({ ...r }))
          : [{ ...emptyRow }];

      setRows(newRows);

      if (newRows.length > 0) {
        setSelectedRow(0);
      }
    } else {
      setGlobalNote("");
      setSelectedRow(null);
    }
  }, [open, initialRows]);

  const handleChange = (idx, field, value) => {
    setRows((rows) =>
      rows.map((row, i) => {
        if (i === idx) {
          const updatedRow = { ...row, [field]: value, error: false };

          if (field === "status") {
            updatedRow.outcome = "";
          }
          return updatedRow;
        }
        return row;
      }),
    );
  };

  const handleNoteClick = (event, idx) => {
    if (noteAnchor && noteRowIdx === idx) {
      setNoteAnchor(null);
      setNoteRowIdx(null);
      setNoteValue("");
      return;
    }
    setNoteAnchor(event.currentTarget);
    setNoteRowIdx(idx);
    setNoteValue(rows[idx].note || "");
  };

  const handleNoteClose = () => {
    setNoteAnchor(null);
    setNoteRowIdx(null);
    setNoteValue("");
  };

  const handleNoteSave = () => {
    setRows((rows) =>
      rows.map((row, i) =>
        i === noteRowIdx ? { ...row, note: noteValue } : row,
      ),
    );
    handleNoteClose();
  };

  const handleRowClick = (idx) => {
    if (
      rows &&
      rows.length > 0 &&
      (!rows[idx] || Object.keys(rows[idx]).length === 0)
    ) {
      if (initialRows?.[idx]) {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          newRows[idx] = { ...initialRows[idx] };
          return newRows;
        });
      } else {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          newRows[idx] = { ...emptyRow };
          return newRows;
        });
      }
    }
    setSelectedRow(idx);
  };

  const ensureRowsInitialized = () => {
    setRows((prevRows) => {
      if (!prevRows || prevRows.length === 0) {
        return [{ ...emptyRow }];
      }

      return prevRows.map((row) => ({
        ...emptyRow,
        ...row,
      }));
    });
  };

  const handleSubmit = () => {
    ensureRowsInitialized();
    let valid = true;
    const newRows = rows.map((row) => {
      let isFilled;
      if (
        isOutreachContext() &&
        memberDrugMap &&
        Object.keys(memberDrugMap).length > 0
      ) {
        isFilled = [
          "program",
          "reasonForOutreach",
          "status",
          "method",
          "outcome",
          "globalNote",
        ].every((key) => row[key] && String(row[key]).trim() !== "");
      } else {
        isFilled = [
          "program",
          "medication",
          "status",
          "method",
          "outcome",
        ].every((key) => row[key] && String(row[key]).trim() !== "");
      }
      if (!isFilled) {
        valid = false;
        return { ...row, error: true };
      }
      return { ...row, error: false };
    });
    setRows(newRows);
    if (!valid) return;
    setInitialRows(newRows);
    setConfirmationOpen(true);
  };

  const handleConfirmDisposition = async () => {
    setIsSubmitting(true);
    setConfirmationOpen(false);

    const payload = rows.map((row, idx) =>
      createDispositionPayload(
        row,
        idx,
        dispositionOpportunity,
        memberDrugMap,
        globalNote,
        type,
      ),
    );

    try {
      await completeManualTask(payload);

      setIsSubmitting(false);
      onSave(rows);
      setConfirmationOpen(false);

      onClose();

      setIsSubmitting(false);
    } catch (err) {
      setIsSubmitting(false);

      if (err?.response?.data?.message?.includes("Invalid status transition")) {
        alert(err.response.data.message);
      } else {
        console.error("In else block");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectOpen = (event, idx, field) => {
    setSelectPopover({ idx, field, anchorEl: event.currentTarget });
  };
  const handleSelectClose = () => {
    setSelectPopover({ idx: null, field: null, anchorEl: null });
  };

  const getAvailableProgramsForRow = (currentIdx) => {
    const selectedPrograms = new Set(
      rows
        .filter((row, idx) => idx !== currentIdx && row.program)
        .map((row) => row.program),
    );

    return programOptions.filter(
      (program) => !selectedPrograms.includes(program),
    );
  };

  const getMonitoringReasonForDrug = (program, drugName) => {
    if (!programDrugMap?.[program]) return "";

    if (
      programDrugMap[program].monitoringReasons &&
      Array.isArray(programDrugMap[program].monitoringReasons)
    ) {
      const drugIndex = programDrugMap[program].drugs.findIndex((drugStr) =>
        drugStr.startsWith(drugName + "-"),
      );

      if (
        drugIndex >= 0 &&
        programDrugMap[program].monitoringReasons[drugIndex]
      ) {
        return programDrugMap[program].monitoringReasons[drugIndex];
      }
    }

    return "";
  };

  const handleSelectValue = (value) => {
    if (selectPopover.idx !== null && selectPopover.field) {
      if (selectPopover.field === "program") {
        setRows((prev) =>
          prev.map((row, i) =>
            i === selectPopover.idx
              ? {
                  ...row,
                  [selectPopover.field]: value,
                  medication: "",
                }
              : row,
          ),
        );
      } else if (selectPopover.field === "medication") {
        const rowProgram = rows[selectPopover.idx].program;
        let drugName = "";
        let monitoringReason = "";

        if (value?.drug) {
          drugName = value.drug;
        } else if (typeof value === "string") {
          drugName = value;
        }

        if (drugName && rowProgram) {
          monitoringReason = getMonitoringReasonForDrug(rowProgram, drugName);
        }

        setRows((prev) =>
          prev.map((row, i) =>
            i === selectPopover.idx
              ? {
                  ...row,
                  [selectPopover.field]: value,

                  ...(selectPopover.field === "status" ? { outcome: "" } : {}),
                  monitoringReason: monitoringReason,
                }
              : row,
          ),
        );
      } else {
        handleChange(selectPopover.idx, selectPopover.field, value);
      }
    }
    handleSelectClose();
  };

  const handleDeleteRow = (idx) => {
    setRows((rows) => {
      const newRows = rows.filter((_, i) => i !== idx);

      if (idx < originalRowCount) {
        setOriginalRowCount((prev) => prev - 1);
      }
      if (noteRowIdx === idx) {
        setNoteAnchor(null);
        setNoteRowIdx(null);
        setNoteValue("");
      }
      if (noteRowIdx > idx) {
        setNoteRowIdx(noteRowIdx - 1);
      }
      return newRows;
    });
  };

  const getMedicationOptionsForRow = (rowProgram, currentIdx) => {
    if (!programDrugMap?.[rowProgram]) return [];

    const drugsArray =
      programDrugMap[rowProgram].drugs || programDrugMap[rowProgram];

    if (!Array.isArray(drugsArray)) return [];

    const selectedDrugKeys = new Set(
      rows
        .filter(
          (row, idx) =>
            idx !== currentIdx && row.program === rowProgram && row.medication,
        )
        .map((row) => row.medication),
    );

    return drugsArray
      .filter((drugStr) => !selectedDrugKeys.includes(drugStr))
      .map((drugStr) => {
        const [drug, drugId, programId] = drugStr.split("-");
        return { drug, drugId, programId, value: drugStr, label: drug };
      });
  };

  const getStatusOptions = (currentIdx = 0) => {
    const initialStatus = initialRows[currentIdx]?.status || "";

    if (showMonitoringReason) {
      if (initialStatus === "Eligible") {
        return ["Eligible", "Enrolled", "Declined"];
      }
      return ["Enrolled", "Declined", "Resolved", "Not Resolved"];
    }
    return ["Eligible", "Enrolled", "Declined"];
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "1235px",
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            position: "relative",
            height: "540px",
            maxHeight: "540px",
          },
        },
      }}
    >
      {" "}
      <Box sx={{ position: "relative", p: 2 }}>
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            width: "100%",
            height: "auto",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            mb: 2,
            position: "relative",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "30px",
              color: "#000000",
              marginBottom: "6px",
              flex: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            {title}
            {title === "Manage Disposition" && (
              <Alert
                variant="outlined"
                severity="error"
                sx={{
                  backgroundColor: "#FFF3F3",
                  boxShadow: "0px 0px 2px 0px #00000040",
                  border: "1px solid #F8D2D2",
                  color: "#6A6A6A",
                  fontWeight: 500,
                  mt: 0,
                  ml: 2,
                  width: "auto",
                  alignItems: "center",
                  display: "flex",
                  py: 0.2,
                  px: 2,
                  "& .MuiAlert-icon": {
                    color: "#6A6A6A",
                    fontSize: "15px",
                  },
                }}
              >
                Make sure the status truly reflects the opportunity.
              </Alert>
            )}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#403C6F",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {noteAnchor && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "16px",
              zIndex: 1,
              p: 0,
              m: 0,
              pointerEvents: "none",
            }}
          />
        )}
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "1200px",
            minHeight: "400px",
            borderRadius: "16px",
            border: "1px solid #E0E0E0",
            gap: "0px",
            m: 0,
            backgroundColor: "#FFFFFF",
            position: "relative",
            zIndex: 0,
            padding: "6px 18px !important",
          }}
        >
          <Box sx={{ pt: "10px !important" }}>
            <div>
              <span style={{ color: "#403C6F" }}>Date :</span>{" "}
              <span style={{ fontSize: "16px", color: "#3A3765" }}>
                {new Date().toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
            <TableContainer sx={{ mt: 2, maxHeight: 260, overflowY: "auto" }}>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiTableCell-root": {
                    color: "#403C6F",
                    fontWeight: 580,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    padding: "6px 8px",
                    borderBottom: "none",
                  },
                  "& thead .MuiTableCell-root": {
                    borderRight: "1px solid #fff",
                    borderLeft: "1px solid #fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#CFCEDA",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#CFCEDA",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    {isOutreachContext() ? (
                      <TableCell sx={{ width: "18%" }}>Member</TableCell>
                    ) : (
                      <TableCell sx={{ width: "18%" }}>Program</TableCell>
                    )}
                    {isOutreachContext() ? (
                      <TableCell sx={{ width: "18%" }}>
                        Reason for Outreach
                      </TableCell>
                    ) : (
                      <TableCell sx={{ width: "18%" }}>Medication</TableCell>
                    )}
                    {(memberBadgeValue === "Member Monitoring" ||
                      showMonitoringReason) && (
                      <TableCell sx={{ width: "18%" }}>
                        Monitoring Reason
                      </TableCell>
                    )}
                    <TableCell sx={{ width: "18%" }}>Status</TableCell>
                    <TableCell sx={{ width: "18%" }}>Outreach Method</TableCell>
                    <TableCell sx={{ width: "18%" }}>
                      Outreach Outcome
                    </TableCell>
                    <TableCell
                      sx={{ width: "5%", textAlign: "center" }}
                    ></TableCell>
                    <TableCell
                      sx={{ width: "5%", textAlign: "center" }}
                    ></TableCell>{" "}
                    {/* Minus icon column */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{ color: "#9F9DB6" }}
                      >
                        No dispositions
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, idx) => (
                      <DispositionRow
                        key={`disposition-row-${row.program || ""}-${
                          row.medication || ""
                        }-${row.status || ""}-${row.method || ""}-${
                          row.outcome || ""
                        }-${idx}`}
                        row={row}
                        idx={idx}
                        title={title}
                        isOutreachContext={isOutreachContext}
                        handleRowClick={handleRowClick}
                        selectedRow={selectedRow}
                        originalRowCount={originalRowCount}
                        handleSelectOpen={handleSelectOpen}
                        selectPopover={selectPopover}
                        handleSelectClose={handleSelectClose}
                        memberDrugMap={memberDrugMap}
                        pharmacyMemberOptions={pharmacyMemberOptions}
                        getAvailableProgramsForRow={getAvailableProgramsForRow}
                        handleSelectValue={handleSelectValue}
                        getMedicationOptionsForRow={getMedicationOptionsForRow}
                        getMonitoringReasonForDrug={getMonitoringReasonForDrug}
                        memberBadgeValue={memberBadgeValue}
                        showMonitoringReason={showMonitoringReason}
                        handleChange={handleChange}
                        noteAnchor={noteAnchor}
                        noteRowIdx={noteRowIdx}
                        handleNoteClick={handleNoteClick}
                        handleNoteClose={handleNoteClose}
                        noteValue={noteValue}
                        setNoteValue={setNoteValue}
                        handleNoteSave={handleNoteSave}
                        handleDeleteRow={handleDeleteRow}
                        rows={rows}
                        getOutreachOptions={getOutreachOptions}
                        getOutreachMethods={getOutreachMethods}
                        initialRows={initialRows}
                        getOutreachStatusOptions={getOutreachStatusOptions}
                        getStatusOptions={getStatusOptions}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  position: "relative",
                  backgroundColor: "#F7F8FB",
                  width: "1150px",
                  height: "172px",
                  p: "14.69px",
                  gap: "11.75px",
                  opacity: 1,
                  borderTopLeftRadius: "11.75px",
                  borderTopRightRadius: "11.75px",
                  borderBottomRightRadius: "11.75px",
                  borderBottomLeftRadius: "11.75px",
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder={
                    type === "provider"
                      ? "Enter provider note details"
                      : "Enter pharmacy note details"
                  }
                  value={globalNote}
                  onChange={(e) =>
                    setGlobalNote(
                      e.target.value.slice(0, GLOBAL_NOTE_CHAR_LIMIT),
                    )
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: GLOBAL_NOTE_CHAR_LIMIT,
                      "aria-label":
                        type === "provider"
                          ? "Provider notes"
                          : "Pharmacy notes",
                    },
                  }}
                  variant="outlined"
                  sx={{
                    width: "1100.63px",
                    height: "142.63px",
                    opacity: 1,
                    "& .MuiOutlinedInput-root": {
                      border: "0.73px solid #E9E9E8",
                      borderRadius: "5.87px",
                      backgroundColor: "#FFFFFF",
                      height: "142.63px",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& textarea": {
                        padding: "2px",
                        boxSizing: "border-box",
                        overflow: "auto !important",
                        maxHeight: "140px",
                        minHeight: "140px",
                        paddingTop: "14.69px",
                        backgroundColor: "transparent",
                      },
                    },
                  }}
                />

                <span
                  style={{
                    position: "absolute",
                    bottom: 20,
                    right: 40,
                    fontSize: "12px",
                    color:
                      globalNote.length >= GLOBAL_NOTE_CHAR_LIMIT
                        ? "#d32f2f"
                        : "#666666",
                    pointerEvents: "none",
                    fontWeight: 400,
                  }}
                  aria-live="polite"
                >
                  {globalNote.length}/{GLOBAL_NOTE_CHAR_LIMIT}
                </span>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-end",
            gap: "10px",
            mt: 0.5,
            position: "relative",
            zIndex: 0,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              height: 38,
              gap: "10px",
              opacity: 1,
              borderRadius: "52px",
              borderWidth: "1px",
              color: "#403C6F",
              border: "1px solid #9F9DB6",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            aria-disabled={!canSubmit || isSubmitting}
            sx={{
              height: 38,
              gap: "10px",
              opacity: 1,
              borderRadius: "52px",
              borderWidth: "1px",
              backgroundColor: (() => {
                if (isSubmitting) {
                  return "#908f9bff";
                } else if (canSubmit) {
                  return "#3F3D56";
                } else {
                  return "#E8E8E9";
                }
              })(),
              color: (() => {
                if (isSubmitting) {
                  return "#FFFFFF";
                } else if (canSubmit) {
                  return "#FFFFFF";
                } else {
                  return "#9F9DB6";
                }
              })(),
              cursor: (() => {
                if (isSubmitting) {
                  return "not-allowed";
                } else if (canSubmit) {
                  return "pointer";
                } else {
                  return "not-allowed";
                }
              })(),
              pointerEvents: !isSubmitting && canSubmit ? "auto" : "none",
              transition: "background-color 0.25s ease, color 0.25s ease",
              boxShadow: canSubmit ? "0px 2px 4px rgba(0,0,0,0.15)" : "none",
              "&:hover": {
                backgroundColor: canSubmit ? "#2F2C46" : "#E8E8E9",
              },
              "&:focus-visible": {
                outline: canSubmit ? "2px solid #2F2C46" : "none",
                outlineOffset: 2,
              },
              "&.Mui-disabled": {
                backgroundColor: "#E8E8E9",
                color: "#9F9DB6",
              },
            }}
            disabled={!canSubmit || isSubmitting}
          >
            {(() => {
              if (isSubmitting) {
                return "Submitting disposition...";
              } else if (title === "Add Disposition") {
                return "Add Disposition";
              } else {
                return "Submit Disposition";
              }
            })()}
          </Button>
        </DialogActions>
        <ConfirmationDialog
          open={confirmationOpen}
          title="Confirm Disposition"
          content="Are you sure you want to submit this disposition?"
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleConfirmDisposition}
          icon={<img src={SaveIcon} alt="save" width={20} height={20} />}
          confirmText={"Confirm"}
          message={rows
            .map((row, idx) => {
              const memberData = dispositionOpportunity?.data?.[idx];
              return `${idx + 1}.  Member: ${
                memberData?.category
                  ? capitalizeFirstLetter(memberData.category)
                  : "Member"
              }, Outcome: ${row.outcome || "-"}`;
            })
            .join("\n")}
        />
      </Box>
    </Dialog>
  );
};

OutreachDispositionModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  title: PropTypes.string,
  dispositionOpportunity: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  programOptions: PropTypes.array,
  programDrugMap: PropTypes.object,
  showMonitoringReason: PropTypes.bool,
  memberBadgeValue: PropTypes.string,
  memberDrugMap: PropTypes.object,
  type: PropTypes.string,
};

export default OutreachDispositionModal;
